import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { dashboardApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Warning,
  CaretRight,
  Calendar,
  Money,
  ArrowsClockwise,
  Phone,
  Car,
  MagnifyingGlass,
  X,
  Receipt,
  ShieldWarning,
  Wrench
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Alerts = () => {
  const { user } = useAuth();
  const isAdminOrGerente = user && (user.role === 'admin' || user.role === 'gerente');
  const [alerts, setAlerts] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [penaltyStatus, setPenaltyStatus] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await dashboardApi.getAlerts();
      setAlerts(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const [expiringPolicies, setExpiringPolicies] = useState([]);
  const [expiringMaintenance, setExpiringMaintenance] = useState([]);

  const loadExpiringContracts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/expiring-contracts?days=180`);
      setExpiringContracts(data);
    } catch (err) {
      console.warn('[Alerts] Could not load expiring contracts:', err);
    }
  }, []);

  const loadExpiringPolicies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/expiring-policies?days=30`);
      setExpiringPolicies(data);
    } catch (err) {
      console.warn('[Alerts] Could not load expiring policies:', err);
    }
  }, []);

  const loadExpiringMaintenance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/expiring-maintenance?days=30`);
      setExpiringMaintenance(data);
    } catch (err) {
      console.warn('[Alerts] Could not load expiring maintenance:', err);
    }
  }, []);

  const loadPenaltyStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/penalties/status`);
      setPenaltyStatus(data);
    } catch (err) {
      console.warn('[Alerts] Could not load penalty status:', err);
    }
  }, []);

  const recalcPenalties = async () => {
    try {
      setRecalculating(true);
      await axios.post(`${API}/penalties/apply-now`);
      await loadPenaltyStatus();
      await loadAlerts();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => { loadAlerts(); loadPenaltyStatus(); loadExpiringContracts(); loadExpiringPolicies(); loadExpiringMaintenance(); }, [loadAlerts, loadPenaltyStatus, loadExpiringContracts, loadExpiringPolicies, loadExpiringMaintenance]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  };

  const filteredAlerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return alerts.filter((a) => {
      if (filter !== 'all' && a.severity !== filter) return false;
      if (!q) return true;
      return (
        (a.client_name || '').toLowerCase().includes(q) ||
        (a.vehicle_plates || '').toLowerCase().includes(q) ||
        (a.vehicle_label || '').toLowerCase().includes(q) ||
        (a.client_phone || '').includes(q)
      );
    });
  }, [alerts, filter, search]);

  const totalAmountRisk = useMemo(
    () => filteredAlerts.reduce((s, a) => s + (a.total_due || a.amount || 0), 0),
    [filteredAlerts]
  );
  const totalRent = useMemo(
    () => filteredAlerts.reduce((s, a) => s + (a.amount || 0), 0),
    [filteredAlerts]
  );
  const totalPenalty = useMemo(
    () => filteredAlerts.reduce((s, a) => s + (a.penalty_amount || 0), 0),
    [filteredAlerts]
  );
  const totalAccessory = useMemo(
    () => filteredAlerts.reduce((s, a) => s + (a.accessory_amount || 0), 0),
    [filteredAlerts]
  );
  const uniqueDrivers = useMemo(
    () => new Set(filteredAlerts.map((a) => a.client_id).filter(Boolean)).size,
    [filteredAlerts]
  );

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high':
        return 'Alta';
      case 'medium':
        return 'Media';
      default:
        return 'Baja';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Alertas de Cobranza</h1>
          <p className="text-[#64748B]">{alerts.length} alertas activas</p>
        </div>
        <button
          onClick={loadAlerts}
          className="btn-secondary flex items-center gap-2"
          data-testid="refresh-alerts-btn"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="alert alert-danger flex items-center gap-2">
          <Warning size={20} />
          {error}
        </div>
      )}

      {/* Penalty Cron Panel */}
      {penaltyStatus && (
        <div className="card p-5" data-testid="penalty-cron-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Money size={20} className="text-[#0055FF]" />
                <h2 className="font-heading font-bold text-lg text-[#0F172A]">Penalizaciones diarias ($100/día)</h2>
              </div>
              <p className="text-sm text-[#64748B]">
                Última ejecución automática: <span className="font-medium text-[#0F172A]">{formatDateTime(penaltyStatus.last_run_at)}</span>
                {' '}— {penaltyStatus.last_updated_count || 0} cuotas actualizadas
              </p>
              <p className="text-xs text-[#64748B] mt-1">
                El cron corre todos los días a las 04:00 UTC sobre cuotas con fecha vencida.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 min-w-[260px]">
              <div className="text-center">
                <p className="text-xs text-[#64748B]">Cuotas atrasadas</p>
                <p className="text-2xl font-bold text-[#E11D48]" data-testid="overdue-count">
                  {penaltyStatus.current?.overdue_count || 0}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#64748B]">Penalización acumulada</p>
                <p className="text-2xl font-bold text-[#E11D48]" data-testid="total-penalty">
                  {formatCurrency(penaltyStatus.current?.total_penalty_final || 0)}
                </p>
              </div>
            </div>
            {isAdminOrGerente && (
              <button
                onClick={recalcPenalties}
                disabled={recalculating}
                className="btn-primary flex items-center gap-2"
                data-testid="recalc-penalties-btn"
              >
                <ArrowsClockwise size={18} className={recalculating ? 'animate-spin' : ''} />
                {recalculating ? 'Recalculando...' : 'Recalcular ahora'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="kpi-card">
          <p className="kpi-label">Conductores en riesgo</p>
          <p className="text-2xl font-bold text-[#0055FF]" data-testid="kpi-unique-drivers">{uniqueDrivers}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">{filteredAlerts.length} cuotas</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Alta (≥30 días)</p>
          <p className="text-2xl font-bold text-[#E11D48]">
            {alerts.filter((a) => a.severity === 'high').length}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Media (14-29)</p>
          <p className="text-2xl font-bold text-[#F59E0B]">
            {alerts.filter((a) => a.severity === 'medium').length}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Baja (1-13)</p>
          <p className="text-2xl font-bold text-[#3B82F6]">
            {alerts.filter((a) => a.severity === 'low').length}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Monto en Riesgo</p>
          <p className="text-xl font-bold text-[#E11D48]" data-testid="total-risk">{formatCurrency(totalAmountRisk)}</p>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">Renta+Mora+Acc</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Mora acumulada</p>
          <p className="text-xl font-bold text-[#E11D48]" data-testid="total-penalty-alerts">{formatCurrency(totalPenalty)}</p>
          {totalAccessory > 0 && (
            <p className="text-[10px] text-[#0055FF] mt-0.5">+{formatCurrency(totalAccessory)} acc.</p>
          )}
        </div>
      </div>

      {/* Contratos por Vencer (180 días) */}
      {expiringContracts.length > 0 && (
        <div className="card p-5" data-testid="expiring-contracts-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-amber-600" />
              <h2 className="font-heading font-bold text-lg text-[#0F172A]">Contratos por Vencer</h2>
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                {expiringContracts.length} en próximos 180 días
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"/>≤30d</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"/>≤90d</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>≤180d</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table-base" data-testid="expiring-contracts-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Cliente</th>
                  <th>Vehículo</th>
                  <th>Fin Contrato</th>
                  <th>Días restantes</th>
                  <th>Renta Sem.</th>
                  <th>Teléfono</th>
                </tr>
              </thead>
              <tbody>
                {expiringContracts.map((e) => (
                  <tr key={e.contract_id} data-testid={`expiring-${e.contract_id}`}>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        e.severity === 'critical' ? 'bg-red-50 text-red-700 border border-red-200' :
                        e.severity === 'high' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {e.severity === 'critical' ? 'Crítico' : e.severity === 'high' ? 'Urgente' : 'Próximo'}
                      </span>
                    </td>
                    <td className="text-sm font-medium">{e.client_name}</td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1">
                        <Car size={12} className="text-[#94A3B8]" />
                        <span>{e.vehicle_label}</span>
                        <span className="text-[#94A3B8]">{e.vehicle_plates}</span>
                      </div>
                    </td>
                    <td className="text-sm font-semibold">{e.end_date}</td>
                    <td>
                      <span className={`text-sm font-bold ${
                        e.days_until_end <= 0 ? 'text-red-700' :
                        e.days_until_end <= 30 ? 'text-red-600' :
                        e.days_until_end <= 90 ? 'text-orange-600' : 'text-amber-600'
                      }`}>
                        {e.days_until_end <= 0 ? `Venció hace ${Math.abs(e.days_until_end)}d` : `${e.days_until_end}d`}
                      </span>
                    </td>
                    <td className="text-sm">{formatCurrency(e.weekly_rent)}</td>
                    <td className="text-xs text-[#64748B]">
                      {e.client_phone ? (
                        <a href={`tel:${e.client_phone}`} className="flex items-center gap-1 hover:text-[#0055FF]">
                          <Phone size={12} />
                          {e.client_phone}
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pólizas por Vencer (30 días) */}
      {expiringPolicies.length > 0 && (
        <div className="card p-5" data-testid="expiring-policies-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldWarning size={20} className="text-orange-600" />
              <h2 className="font-heading font-bold text-lg text-[#0F172A]">Pólizas por Vencer</h2>
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                {expiringPolicies.length} en próximos 30 días
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"/>Vencida</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"/>≤15d</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>≤30d</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table-base" data-testid="expiring-policies-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Cliente</th>
                  <th>Póliza</th>
                  <th>Vigencia</th>
                  <th>Días restantes</th>
                  <th>Teléfono</th>
                </tr>
              </thead>
              <tbody>
                {expiringPolicies.map((p) => (
                  <tr key={p.file_id} data-testid={`expiring-policy-${p.file_id}`}>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        p.severity === 'critical' ? 'bg-red-50 text-red-700 border border-red-200' :
                        p.severity === 'high' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {p.severity === 'critical' ? 'Vencida' : p.severity === 'high' ? 'Urgente' : 'Próxima'}
                      </span>
                    </td>
                    <td className="text-sm font-medium">{p.client_name}</td>
                    <td className="text-xs text-[#64748B] truncate max-w-[200px]" title={p.original_name}>{p.original_name}</td>
                    <td className="text-sm font-semibold">{p.expiry_date}</td>
                    <td>
                      <span className={`text-sm font-bold ${
                        p.days_until_expiry <= 0 ? 'text-red-700' :
                        p.days_until_expiry <= 15 ? 'text-red-600' :
                        'text-amber-600'
                      }`}>
                        {p.message}
                      </span>
                    </td>
                    <td className="text-xs text-[#64748B]">
                      {p.client_phone ? (
                        <a href={`tel:${p.client_phone}`} className="flex items-center gap-1 hover:text-[#0055FF]">
                          <Phone size={12} />
                          {p.client_phone}
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mantenimientos por Vencer (30 días o km) */}
      {expiringMaintenance.length > 0 && (
        <div className="card p-5" data-testid="expiring-maintenance-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wrench size={20} className="text-purple-600" />
              <h2 className="font-heading font-bold text-lg text-[#0F172A]">Mantenimientos por Vencer</h2>
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-medium">
                {expiringMaintenance.length} en próximos 30 días
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"/>Vencido</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"/>≤15d</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>≤30d</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table-base" data-testid="expiring-maintenance-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Vehículo</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Próximo servicio</th>
                  <th>Restante</th>
                </tr>
              </thead>
              <tbody>
                {expiringMaintenance.map((m, i) => (
                  <tr key={`${m.vehicle_id}-${m.maintenance_type}-${i}`} data-testid={`expiring-maint-${m.vehicle_id}-${m.maintenance_type}`}>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        m.severity === 'critical' ? 'bg-red-50 text-red-700 border border-red-200' :
                        m.severity === 'high' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {m.severity === 'critical' ? 'Vencido' : m.severity === 'high' ? 'Urgente' : 'Próximo'}
                      </span>
                    </td>
                    <td className="text-sm">
                      <div className="font-medium">{m.vehicle_label || '—'}</div>
                      <div className="text-xs text-[#64748B]">{m.vehicle_plates}</div>
                    </td>
                    <td className="text-sm text-[#0055FF] font-medium">{m.client_name}</td>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${m.maintenance_type === 'mayor' ? 'bg-[#F5F3FF] text-[#7C3AED]' : 'bg-[#EFF6FF] text-[#0055FF]'}`}>
                        {m.maintenance_type === 'mayor' ? 'Mayor' : 'Menor'}
                      </span>
                    </td>
                    <td className="text-sm">
                      {m.next_date ? <div className="font-semibold">{m.next_date}</div> : <span className="text-[#94A3B8]">—</span>}
                      {m.next_km ? <div className="text-xs text-[#64748B]">{new Intl.NumberFormat('es-MX').format(m.next_km)} km</div> : null}
                    </td>
                    <td>
                      <span className={`text-sm font-bold ${
                        m.severity === 'critical' ? 'text-red-700' :
                        m.severity === 'high' ? 'text-red-600' :
                        'text-amber-600'
                      }`}>
                        {m.message}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por conductor, placa, vehículo o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 pr-10"
            data-testid="alerts-search"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
              aria-label="Limpiar"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {['all', 'high', 'medium', 'low'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-[#0055FF] text-white'
                  : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? `Todas (${alerts.length})` : getSeverityLabel(f)}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <div className="card p-8 text-center">
            <Warning size={48} className="mx-auto text-[#64748B] mb-4" />
            <p className="text-[#64748B]">
              {search ? `Sin coincidencias para "${search}"` : 'No hay alertas que mostrar'}
            </p>
          </div>
        ) : (
          filteredAlerts.map((alert, index) => (
            <div
              key={`${alert.contract_id}-${alert.due_date}-${alert.week_number}-${index}`}
              className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}
              data-testid={`alert-item-${index}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Header row */}
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <Warning size={20} className={alert.severity === 'high' ? 'text-red-600' : alert.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'} />
                    <span className="font-semibold text-[#0F172A]">{alert.client_name}</span>
                    <span className={`badge ${
                      alert.severity === 'high' ? 'badge-danger' :
                      alert.severity === 'medium' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {getSeverityLabel(alert.severity)}
                    </span>
                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                      {alert.days_late} días de atraso
                    </span>
                  </div>

                  {/* Detail row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {alert.client_phone && (
                      <div className="flex items-center gap-1.5 text-[#475569]">
                        <Phone size={14} className="text-[#94A3B8]" />
                        <a
                          href={`tel:${alert.client_phone}`}
                          className="hover:underline hover:text-[#0055FF]"
                          data-testid={`alert-phone-${index}`}
                        >
                          {alert.client_phone}
                        </a>
                      </div>
                    )}
                    {alert.vehicle_plates && (
                      <div className="flex items-center gap-1.5 text-[#475569]">
                        <Car size={14} className="text-[#94A3B8]" />
                        <span className="truncate" title={`${alert.vehicle_label} (${alert.vehicle_plates})`}>
                          {alert.vehicle_label} <span className="text-[#94A3B8]">({alert.vehicle_plates})</span>
                        </span>
                      </div>
                    )}
                    {alert.week_number != null && (
                      <div className="flex items-center gap-1.5 text-[#475569]">
                        <Receipt size={14} className="text-[#94A3B8]" />
                        Semana {alert.week_number} del contrato
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-[#475569]">
                      <Calendar size={14} className="text-[#94A3B8]" />
                      Vence {alert.due_date}
                    </div>
                  </div>

                  {/* Breakdown row */}
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm mt-3 pt-3 border-t border-current/10">
                    <div>
                      <span className="text-[#64748B] text-xs">Renta: </span>
                      <span className="font-medium">{formatCurrency(alert.amount)}</span>
                    </div>
                    {alert.penalty_amount > 0 && (
                      <div>
                        <span className="text-[#64748B] text-xs">Mora: </span>
                        <span className="font-medium text-[#E11D48]">{formatCurrency(alert.penalty_amount)}</span>
                      </div>
                    )}
                    {alert.accessory_amount > 0 && (
                      <div>
                        <span className="text-[#64748B] text-xs">Accesorios: </span>
                        <span className="font-medium text-[#0055FF]">{formatCurrency(alert.accessory_amount)}</span>
                      </div>
                    )}
                    <div className="ml-auto">
                      <span className="text-[#64748B] text-xs">Total a cobrar: </span>
                      <span className="font-bold text-[#F59E0B] text-base">{formatCurrency(alert.total_due || alert.amount)}</span>
                    </div>
                  </div>
                </div>

                <Link
                  to="/payments"
                  className="p-2 hover:bg-white/50 rounded transition-colors flex-shrink-0"
                  title="Ir a Pagos para registrar cobro"
                  data-testid={`view-payments-${index}`}
                >
                  <CaretRight size={20} />
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Alerts;
